import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { NormalizedCarbonEmissionsData } from "./normalizeInput";
import { fetchPreviousYearMonthlyCarbonEmissionsThunk } from "./thunks/fetchOrganizationCarbonEmissions.thunk";

/**
 * Redux state for a single organization's carbon emissions data.
 *
 * This type represents the complete state for one organization, including
 * the emissions data, loading status, error information, and retry control.
 */
export type OrganizationCarbonEmissionsState = {
  /** Normalized emissions data or null if not loaded */
  data: NormalizedCarbonEmissionsData | null;

  /** True when actively fetching data from the API */
  isLoading: boolean;

  /** Human-readable error message if fetch failed, null otherwise */
  error?: string | null;

  /**
   * Flag indicating this error should not be retried.
   *
   * Purpose: Prevents infinite retry loops for permanent errors like 404/403/401/400.
   * When true, the UI should display a permanent error state instead of a loading
   * spinner, and the `ensureOrganizationCarbonEmissionsLoaded` thunk will skip
   * attempting to refetch the data.
   *
   * UI Behavior:
   * - true: Show permanent error message, no retry button
   * - false: Show temporary error message, allow retry
   *
   * Set by: `fetchPreviousYearMonthlyCarbonEmissionsThunk` error handler
   * Used by: `ensureOrganizationCarbonEmissionsLoaded` skip logic
   */
  isNonRetryableError?: boolean;
};

/**
 * Root carbon emissions Redux slice state.
 *
 * Organizes emissions data by organization ID for efficient lookup and updates.
 * Each organization's data is stored independently to avoid unnecessary re-renders
 * when switching between organizations.
 *
 * Structure: Normalized state pattern (byId)
 * - Key: organizationId (UUID)
 * - Value: OrganizationCarbonEmissionsState
 */
export type CarbonEmissionsSliceState = {
  byOrganizationId: Record<string, OrganizationCarbonEmissionsState>;
};

export const initialState: CarbonEmissionsSliceState = {
  byOrganizationId: {}
};

const carbonEmissionsSlice = createSlice({
  name: "carbonEmissions",
  initialState,
  reducers: {
    setOrganizationLoading: (
      state,
      action: PayloadAction<{
        organizationId: string;
        isLoading: boolean;
      }>
    ) => {
      const { organizationId, isLoading } = action.payload;

      if (!state.byOrganizationId[organizationId]) {
        state.byOrganizationId[organizationId] = {
          data: null,
          isLoading: isLoading,
          error: null,
          isNonRetryableError: false
        };
      } else {
        state.byOrganizationId[organizationId].isLoading = isLoading;
      }
    },
    clearCarbonEmissionsData: (
      state,
      action: PayloadAction<{ organizationId: string }>
    ) => {
      const { organizationId } = action.payload;
      if (state.byOrganizationId[organizationId]) {
        state.byOrganizationId[organizationId] = {
          data: null,
          isLoading: false,
          error: null,
          isNonRetryableError: false
        };
      }
    }
  },
  extraReducers: builder => {
    builder
      // Handle fetchPreviousYearMonthlyCarbonEmissionsThunk
      .addCase(
        fetchPreviousYearMonthlyCarbonEmissionsThunk.pending,
        (state, action) => {
          const organizationId = action.meta.arg;
          state.byOrganizationId[organizationId] = {
            ...(state.byOrganizationId[organizationId] || { data: null }),
            isLoading: true,
            error: null,
            isNonRetryableError: false
          };
        }
      )
      .addCase(
        fetchPreviousYearMonthlyCarbonEmissionsThunk.fulfilled,
        (state, action) => {
          const { organizationId, data } = action.payload;
          state.byOrganizationId[organizationId] = {
            data,
            isLoading: false,
            error: null,
            isNonRetryableError: false
          };
        }
      )
      .addCase(
        fetchPreviousYearMonthlyCarbonEmissionsThunk.rejected,
        (state, action) => {
          const error = action.payload as {
            error: string;
            organizationId?: string;
            isNonRetryable?: boolean;
          };
          const organizationId = error.organizationId || action.meta.arg;

          if (organizationId) {
            if (!state.byOrganizationId[organizationId]) {
              state.byOrganizationId[organizationId] = {
                data: null,
                isLoading: false,
                error: error.error,
                isNonRetryableError: error.isNonRetryable || false
              };
            } else {
              state.byOrganizationId[organizationId].isLoading = false;
              state.byOrganizationId[organizationId].error = error.error;
              state.byOrganizationId[organizationId].isNonRetryableError =
                error.isNonRetryable || false;
            }
          }
        }
      );
  }
});

export const { clearCarbonEmissionsData, setOrganizationLoading } =
  carbonEmissionsSlice.actions;
export const carbonEmissionsReducer = carbonEmissionsSlice.reducer;
